import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ChargingPointProperties } from '../../interface/charging-point.interface';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { updateSidePanelData } from '../../redux/slices/TableSidePanelSlice';
import device from '../../utils/api/device';
import personalDeviceApi from '../../utils/api/v2/personal-chargers';
import TabToggle from '../tab-toggle/TabToggle';

import ConnectorSidePanel from './ConnectorSidePanel';

import './scss/personal-device-side-panel.scss';

interface PersonalDeviceSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  deviceModule?: boolean;
}

/**
 * Personal Device side panel component
 * @param {PersonalDeviceSidePanelProperties} properties - Component properties
 * @returns {React.ReactElement} Personal device side panel element
 */
export default function PersonalDeviceSidePanel(properties: PersonalDeviceSidePanelProperties) {
  const { t } = useTranslation('sidePanel');
  const { fetchIdFrom, id } = properties;

  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>('deviceDetails');

  const [deviceDetails, setDeviceDetails] = useState<ChargingPointProperties>({
    connectors: [],
    isPersonalCharger: true,
  });

  /**
   * Function to fetch station details
   * @param {string} stationId - station id
   * @returns {Promise<void>} Promise resolving when details are fetched
   */
  const handleFetchStationDetails = async (stationId: string) => {
    personalDeviceApi
      .getPersonalChargingPointById(stationId)
      .then((response) => {
        const data = response?.data?.data || response?.data;
        const formattedData: ChargingPointProperties = {
          id: data?._id || data?.id || stationId,
          ...data,
          chargePointVendor: data?.brandName || data?.chargePointVendor,
          chargePointModel: data?.modelName || data?.chargePointModel,
          userFirstName: data?.firstName || data?.userFirstName,
          userLastName: data?.lastName || data?.userLastName,
          connectors: data?.connectorInformation || data?.connectors || [],
          isPersonalCharger: true,
        };
        setDeviceDetails(formattedData);
        dispatch(
          updateSidePanelData({
            type: 'personalChargerId',
            value: {
              id: formattedData.id || '',
              ...formattedData,
            },
          }),
        );
      })
      .catch(() => {
        device
          .getChargingPointById(stationId)
          .then((response) => {
            const data = response?.data;
            const formattedData: ChargingPointProperties = {
              id: data?._id || '',
              ...data,
              isPersonalCharger: false,
            };
            setDeviceDetails(formattedData);
            dispatch(
              updateSidePanelData({
                type: 'personalChargerId',
                value: {
                  id: formattedData.id || '',
                  ...formattedData,
                },
              }),
            );
          })
          .catch((error) => {
            dispatch(
              setNotificationMessage({
                status: true,
                message: error?.response?.data?.message || t('somethingWentWrong'),
                type: 'error',
                code: 0,
              }),
            );
          });
      });
  };

  useEffect(() => {
    let stationId;
    if (fetchIdFrom === 'queryParams') {
      stationId = searchParameters.get('personalChargerId') || searchParameters.get('deviceId');
    } else if (fetchIdFrom === 'props') {
      stationId = id;
    }

    if (!stationId) {
      return;
    }

    handleFetchStationDetails(stationId);
  }, [searchParameters, id]);

  let plugAndChargeText = t('na');
  if (typeof deviceDetails.plugAndCharge === 'boolean') {
    plugAndChargeText = deviceDetails.plugAndCharge ? t('yes', 'Yes') : t('no', 'No');
  }

  let networkConnectivityText = t('na');
  if (Array.isArray(deviceDetails.networkConnectivity) && deviceDetails.networkConnectivity.length > 0) {
    networkConnectivityText = deviceDetails.networkConnectivity.join(', ');
  } else if (typeof deviceDetails.networkConnectivity === 'string' && deviceDetails.networkConnectivity) {
    networkConnectivityText = deviceDetails.networkConnectivity;
  }

  let protocolSupportText = t('na');
  if (Array.isArray(deviceDetails.protocolSupport) && deviceDetails.protocolSupport.length > 0) {
    protocolSupportText = deviceDetails.protocolSupport.join(', ');
  } else if (typeof deviceDetails.protocolSupport === 'string' && deviceDetails.protocolSupport) {
    protocolSupportText = deviceDetails.protocolSupport;
  }

  let certificationsText = t('na');
  if (Array.isArray(deviceDetails.certifications) && deviceDetails.certifications.length > 0) {
    certificationsText = deviceDetails.certifications.join(', ');
  } else if (typeof deviceDetails.certifications === 'string' && deviceDetails.certifications) {
    certificationsText = deviceDetails.certifications;
  }

  return (
    <div className="personal-device-side-panel-wrap">
      <div className="personal-device-container">
        <div className="tab-toggle-container">
          <TabToggle
            tabs={[
              { label: t('deviceDetails', 'Device Details'), value: 'deviceDetails' },
              { label: t('connectorDetails', 'Connector Details'), value: 'connectorDetails' },
            ]}
            defaultTab="deviceDetails"
            onChange={(value) => setActiveTab(value)}
            isLabelValue
          />
        </div>

        {activeTab === 'deviceDetails' && (
          <div className="panel-section device-detail-sec">
            <div className="device-info-fields">
              <div className="info-group">
                <span className="info-label">{t('status', 'Status')}</span>
                <div
                  className={`device-status-text-container ${deviceDetails.isDeviceOffline ? 'offline' : 'online'}`}>
                  <div className="icon" />
                  <p>{deviceDetails.isDeviceOffline ? t('offline') : t('online')}</p>
                </div>
              </div>

              <div className="info-group">
                <span className="info-label">{t('deviceName', 'Device Name')}</span>
                <p className="info-value">{deviceDetails.chargingPointName || t('na')}</p>
              </div>

              <div className="info-group">
                <span className="info-label">{t('serialNumber', 'Serial Number')}</span>
                <p className="info-value">{deviceDetails.serialNumber || deviceDetails.chargingPointSn || t('na')}</p>
              </div>

              <div className="info-group">
                <span className="info-label">{t('vendor', 'Vendor')}</span>
                <p className="info-value">{deviceDetails.brandName || deviceDetails.chargePointVendor || t('na')}</p>
              </div>

              <div className="info-group">
                <span className="info-label">{t('deviceId', 'Device Id')}</span>
                <p className="info-value">{deviceDetails.deviceId || deviceDetails.chargingPointSn || t('na')}</p>
              </div>

              <div className="info-group">
                <span className="info-label">{t('deviceModel', 'Device Model')}</span>
                <p className="info-value">{deviceDetails.modelName || deviceDetails.chargePointModel || t('na')}</p>
              </div>

              <div className="info-group">
                <span className="info-label">{t('ocppId', 'OCPP ID')}</span>
                <p className="info-value">{deviceDetails.chargingPointSn || t('na')}</p>
              </div>

              <div className="info-group">
                <span className="info-label">{t('deviceBrand', 'Device Brand')}</span>
                <p className="info-value">{deviceDetails.brandName || deviceDetails.chargePointVendor || t('na')}</p>
              </div>

              <div className="info-group">
                <span className="info-label">{t('deviceType', 'Device Type')}</span>
                <p className="info-value">{deviceDetails.deviceType || t('na')}</p>
              </div>

              <div className="info-group">
                <span className="info-label">{t('maxOutputPower', 'Max Output Power')}</span>
                <p className="info-value">
                  {deviceDetails.maximumOutputPower || deviceDetails.specifications?.powerOutput
                    ? `${deviceDetails.maximumOutputPower || deviceDetails.specifications?.powerOutput} ${deviceDetails.maximumOutputPowerUnit || deviceDetails.connectorInformation?.[0]?.unit || 'KW'}`
                    : t('na')}
                </p>
              </div>

              <div className="info-group">
                <span className="info-label">{t('installationType', 'Installation Type')}</span>
                <p className="info-value">{deviceDetails.installationType || t('na')}</p>
              </div>

              <div className="info-group">
                <span className="info-label">{t('phoneNumber', 'Phone Number')}</span>
                <p className="info-value">
                  {deviceDetails.phoneNumber
                    ? `${deviceDetails.countryCode ? `+${deviceDetails.countryCode} ` : ''}${deviceDetails.phoneNumber}`
                    : t('na')}
                </p>
              </div>

              <div className="info-group">
                <span className="info-label">{t('networkConnectivity', 'Network Connectivity')}</span>
                <p className="info-value">{networkConnectivityText}</p>
              </div>

              <div className="info-group">
                <span className="info-label">{t('plugAndCharge', 'Plug and Charge')}</span>
                <p className="info-value">{plugAndChargeText}</p>
              </div>

              <div className="info-group">
                <span className="info-label">{t('protocolSupport', 'Protocol Support')}</span>
                <p className="info-value">{protocolSupportText}</p>
              </div>

              <div className="info-group">
                <span className="info-label">{t('certifications', 'Certifications')}</span>
                <p className="info-value">{certificationsText}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'connectorDetails' && (
          <div className="panel-section connector-detail-sec">
            <ConnectorSidePanel
              connectors={
                deviceDetails.connectorInformation?.length
                  ? deviceDetails.connectorInformation
                  : deviceDetails.connectors
              }
              showHeader={false}
              id={deviceDetails._id || deviceDetails.id}
              chargingPointSn={deviceDetails.chargingPointSn}
            />
          </div>
        )}
      </div>
    </div>
  );
}
